import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CreditCard, Banknote, Plus, Check, Lock, X, Wallet as WalletIcon } from 'lucide-react';
import { walletService } from '../../services/walletService';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';

const PaymentMethods: React.FC<{ onOpenWallet?: () => void }> = ({ onOpenWallet }) => {
  const [selectedMethod, setSelectedMethod] = useState<'cash' | 'card' | 'wallet'>('cash');
  const [saving, setSaving] = useState(false);
  const [showCardForm, setShowCardForm] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardError, setCardError] = useState('');
  const [savedCards, setSavedCards] = useState<any[]>([]);
  const [walletBalance, setWalletBalance] = useState<number | null>(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.paymentMethod) setSelectedMethod(user.paymentMethod);
    const cards = JSON.parse(localStorage.getItem('savedCards') || '[]');
    setSavedCards(cards);
    walletService.getBalance().then(setWalletBalance).catch(() => {});
  }, []);

  const savePaymentMethod = async (method: 'cash' | 'card' | 'wallet') => {
    setSelectedMethod(method);
    setSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      await fetch(`${API_URL}/auth/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ paymentMethod: method }),
      });
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      user.paymentMethod = method;
      localStorage.setItem('user', JSON.stringify(user));
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const validateCard = () => {
    const cleaned = cardNumber.replace(/\s/g, '');
    if (cleaned.length < 13 || cleaned.length > 19) return 'Invalid card number';
    if (!/^\d+$/.test(cleaned)) return 'Card must be numbers only';
    
    // Luhn algorithm check
    let sum = 0;
    let alternate = false;
    for (let i = cleaned.length - 1; i >= 0; i--) {
      let n = parseInt(cleaned[i]);
      if (alternate) { n *= 2; if (n > 9) n -= 9; }
      sum += n;
      alternate = !alternate;
    }
    if (sum % 10 !== 0) return 'Invalid card number';

    if (expiry.length !== 5 || !/^\d{2}\/\d{2}$/.test(expiry)) return 'Invalid expiry (MM/YY)';
    const [month, year] = expiry.split('/').map(Number);
    if (month < 1 || month > 12) return 'Invalid month';
    if (year < 25) return 'Card expired';

    if (cvv.length < 3 || cvv.length > 4) return 'Invalid CVV';
    if (!/^\d+$/.test(cvv)) return 'CVV must be numbers';

    return '';
  };

  const handleSaveCard = () => {
    const error = validateCard();
    if (error) { setCardError(error); return; }
    
    // Save card (masked)
    const last4 = cardNumber.replace(/\s/g, '').slice(-4);
    const newCard = { id: Date.now(), last4, expiry, brand: detectCardBrand(cardNumber) };
    const cards = [...savedCards, newCard];
    setSavedCards(cards);
    localStorage.setItem('savedCards', JSON.stringify(cards));
    
    savePaymentMethod('card');
    setShowCardForm(false);
    setCardNumber('');
    setExpiry('');
    setCvv('');
    setCardError('');
  };

  const detectCardBrand = (num: string): string => {
    const n = num.replace(/\s/g, '');
    if (n.startsWith('4')) return 'Visa';
    if (/^5[1-5]/.test(n)) return 'Mastercard';
    if (/^3[47]/.test(n)) return 'Amex';
    return 'Card';
  };

  const formatCardNumber = (val: string) => {
    const cleaned = val.replace(/\D/g, '').slice(0, 16);
    return cleaned.replace(/(\d{4})(?=\d)/g, '$1 ');
  };

  const formatExpiry = (val: string) => {
    const cleaned = val.replace(/\D/g, '').slice(0, 4);
    if (cleaned.length > 2) return cleaned.slice(0, 2) + '/' + cleaned.slice(2);
    return cleaned;
  };

  const handlePaystackPayment = () => {
    const handler = (window as any).PaystackPop?.setup({
      key: process.env.REACT_APP_PAYSTACK_KEY || 'pk_test_your_paystack_public_key',
      email: JSON.parse(localStorage.getItem('user') || '{}').email || 'user@example.com',
      amount: 100,
      currency: 'KES',
      label: 'SafariCab',
      onSuccess: () => savePaymentMethod('card'),
    });
    handler?.openIframe();
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="px-4 pt-4 pb-24">
      <h2 className="text-white font-bold text-xl mb-6">Payment Methods</h2>

      <button onClick={() => savePaymentMethod('cash')} className={`w-full flex items-center gap-4 p-4 rounded-xl border mb-3 ${selectedMethod === 'cash' ? 'border-safaricab-gold bg-safaricab-gold/10' : 'border-white/10 bg-safaricab-card'}`}>
        <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center"><Banknote className="w-6 h-6 text-green-500" /></div>
        <div className="flex-1 text-left"><p className="text-white font-semibold">Cash</p><p className="text-white/50 text-xs">Pay with cash directly</p></div>
        {selectedMethod === 'cash' && <Check className="w-5 h-5 text-safaricab-gold" />}
      </button>

      <button onClick={() => savePaymentMethod('card')} className={`w-full flex items-center gap-4 p-4 rounded-xl border mb-3 ${selectedMethod === 'card' ? 'border-safaricab-gold bg-safaricab-gold/10' : 'border-white/10 bg-safaricab-card'}`}>
        <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center"><CreditCard className="w-6 h-6 text-blue-500" /></div>
        <div className="flex-1 text-left"><p className="text-white font-semibold">Card</p><p className="text-white/50 text-xs">Visa, Mastercard</p></div>
        {selectedMethod === 'card' && <Check className="w-5 h-5 text-safaricab-gold" />}
      </button>

      <button onClick={() => savePaymentMethod('wallet')} className={`w-full flex items-center gap-4 p-4 rounded-xl border mb-3 ${selectedMethod === 'wallet' ? 'border-safaricab-gold bg-safaricab-gold/10' : 'border-white/10 bg-safaricab-card'}`}>
        <div className="w-12 h-12 rounded-full bg-safaricab-gold/20 flex items-center justify-center"><WalletIcon className="w-6 h-6 text-safaricab-gold" /></div>
        <div className="flex-1 text-left">
          <p className="text-white font-semibold">Wallet</p>
          <p className="text-white/50 text-xs">
            {walletBalance === null ? 'App recharge - no M-Pesa needed' : `Balance: KES ${walletBalance.toLocaleString()}`}
          </p>
        </div>
        {selectedMethod === 'wallet' && <Check className="w-5 h-5 text-safaricab-gold" />}
      </button>

      {selectedMethod === 'wallet' && (
        <button
          onClick={() => onOpenWallet?.()}
          className="w-full bg-white/5 text-safaricab-gold font-medium py-3 rounded-xl text-sm mb-4 border border-white/10"
        >
          Top Up Your SafariCab Account
        </button>
      )}

      {/* Saved Cards */}
      {savedCards.length > 0 && (
        <div className="mt-4 mb-4">
          <h3 className="text-white/60 text-xs font-medium uppercase mb-2">Saved Cards</h3>
          {savedCards.map((card: any) => (
            <div key={card.id} className="flex items-center gap-3 bg-safaricab-card p-3 rounded-xl mb-2 border border-white/10">
              <CreditCard className="w-5 h-5 text-blue-500" />
              <span className="text-white text-sm">{card.brand} ****{card.last4}</span>
              <span className="text-white/50 text-xs ml-auto">{card.expiry}</span>
            </div>
          ))}
        </div>
      )}

      {/* Card Form */}
      {selectedMethod === 'card' && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-4 space-y-3">
          {!showCardForm ? (
            <div className="space-y-3">
              <button onClick={() => setShowCardForm(true)} className="w-full bg-safaricab-gold text-black font-bold py-4 rounded-xl flex items-center justify-center gap-2">
                <Plus className="w-5 h-5" /> Add New Card
              </button>
              <button onClick={handlePaystackPayment} className="w-full bg-white/10 text-white font-medium py-3 rounded-xl text-sm">
                Verify via Paystack
              </button>
            </div>
          ) : (
            <div className="bg-safaricab-card rounded-xl p-4 border border-white/10 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-white font-semibold text-sm">Enter Card Details</h3>
                <button onClick={() => setShowCardForm(false)}><X className="w-4 h-4 text-white/50" /></button>
              </div>
              
              <div className="space-y-2">
                <label className="text-white/60 text-xs">Card Number</label>
                <div className="relative">
                  <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <input type="text" value={cardNumber} onChange={(e) => { setCardNumber(formatCardNumber(e.target.value)); setCardError(''); }} placeholder="4242 4242 4242 4242" maxLength={19} className="w-full bg-safaricab-dark border border-white/10 rounded-xl py-3 pl-10 pr-4 text-white outline-none focus:border-safaricab-gold/50" />
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex-1 space-y-2">
                  <label className="text-white/60 text-xs">Expiry</label>
                  <input type="text" value={expiry} onChange={(e) => { setExpiry(formatExpiry(e.target.value)); setCardError(''); }} placeholder="MM/YY" maxLength={5} className="w-full bg-safaricab-dark border border-white/10 rounded-xl py-3 px-4 text-white outline-none focus:border-safaricab-gold/50" />
                </div>
                <div className="flex-1 space-y-2">
                  <label className="text-white/60 text-xs">CVV</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                    <input type="password" value={cvv} onChange={(e) => { setCvv(e.target.value.replace(/\D/g, '').slice(0, 4)); setCardError(''); }} placeholder="123" maxLength={4} className="w-full bg-safaricab-dark border border-white/10 rounded-xl py-3 pl-10 pr-4 text-white outline-none focus:border-safaricab-gold/50" />
                  </div>
                </div>
              </div>

              {cardError && <p className="text-red-400 text-xs">{cardError}</p>}
              <p className="text-white/30 text-[10px]">Test card: 4242 4242 4242 4242 | Exp: any future date | CVV: any 3 digits</p>

              <button onClick={handleSaveCard} className="w-full bg-green-500 text-black font-bold py-3 rounded-xl flex items-center justify-center gap-2">
                <Lock className="w-4 h-4" /> Save Card
              </button>
            </div>
          )}
          <p className="text-white/30 text-xs text-center">Secured with 256-bit encryption</p>
        </motion.div>
      )}
    </motion.div>
  );
};

export default PaymentMethods;
